import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { X } from 'lucide-react';
import {
  Button,
  type ButtonIntent,
  type ButtonSize,
  type ButtonType,
} from '@/components/ui/atoms/Button';
import { getGTMAttributes, GtmEventKey } from '../../utils/gtm/gtm-tracking';
import {
  getConfirmationDialogButtonTracking,
  getConfirmationDialogCloseTracking,
} from '../../utils/gtm/gtm-confirmation-dialog';

export interface ConfirmationDialogButton {
  label: string;
  onClick: () => void;
  type?: ButtonType;
  intent?: ButtonIntent;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  className?: string;
  size?: ButtonSize;
}

export interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: React.ReactNode;
  icon?: React.ReactNode;
  buttons: ConfirmationDialogButton[];
  /** GTM event key. */
  gtm?: GtmEventKey;
  /** GTM origin (data-origin) */
  origin?: string;
  closeLabel?: string;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  buttons,
  gtm,
  origin,
  closeLabel = 'Close',
}) => {
  const trackingConfig = { gtm, origin };
  const closeButtonTracking = getConfirmationDialogCloseTracking(trackingConfig);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg sm:min-w-md min-w-auto">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {icon && <div className="flex-shrink-0">{icon}</div>}
              <DialogTitle>
                {title}
              </DialogTitle>
            </div>

            {/* Close button */}
            <Button
              {...getGTMAttributes(closeButtonTracking)}
              type="icon"
              intent="muted"
              onClick={onClose}
              icon={<X />}
              label={closeLabel}
            />
          </div>
        </DialogHeader>

        <div className="space-y-7">
          <div className="text-sm text-gray-500">{message}</div>

          <div className="flex justify-end space-x-3">
            {buttons.map((button, index) => {
              const {
                label,
                onClick,
                type,
                intent,
                disabled,
                loading,
                icon: buttonIcon,
                className,
                size,
              } = button;
              const buttonTracking = getConfirmationDialogButtonTracking(
                trackingConfig,
                { intent, isLastButton: index === buttons.length - 1 }
              );
              return (
                <Button
                  key={index}
                  {...getGTMAttributes(buttonTracking)}
                  label={label}
                  onClick={onClick}
                  type={type ?? 'contained'}
                  intent={intent ?? 'secondary'}
                  disabled={disabled}
                  loading={loading}
                  icon={buttonIcon}
                  className={className}
                  size={size}
                />
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
